import type {DeviceId} from "../../models/create-id";
import {oneOffSync} from "../../models/db";
import type {GroupId} from "../../models/GroupRepo";
import {getGroupRepoLoader} from "../../models/GroupRepo";
import type {UserId, UserRepo} from "../../models/UserRepo";
import {StorageKey, storageWrapper} from "../../storage";
import {syncAllLocalAssetsWithNewDeviceId} from "../../utils/file-helpers";
import {request} from "../../utils/request";
import {wsServerHost} from "../../utils/server-hosts";

export const ensureDeviceId = async (userRepo: UserRepo) => {
  const deviceId = storageWrapper.storageGet(StorageKey.DEVICE_ID);
  if (deviceId) return deviceId;
  const {isSyncing} = userRepo.vals.get();
  if (!isSyncing) return null;
  const res = await request<{deviceId: DeviceId}>("GET", `/auth/device-id`);
  setDeviceId(res.deviceId);
  return res.deviceId;
};

const setDeviceId = (deviceId: DeviceId) => {
  storageWrapper.storageSet(StorageKey.DEVICE_ID, deviceId);
};

const setReauthSecret = (reauthSecret: string) => {
  storageWrapper.storageSet(StorageKey.REAUTH_SECRET, reauthSecret);
};

// devices that started syncing before reauth secrets existed need to fetch one while their
// cookies are still valid
let reauthSecretPromise: Promise<void> | null = null;
export const ensureReauthSecret = async (userRepo: UserRepo) => {
  if (reauthSecretPromise) return reauthSecretPromise;
  if (!userRepo.vals.get().isSyncing) return;
  if (storageWrapper.storageGet(StorageKey.REAUTH_SECRET)) return;
  reauthSecretPromise = request<{reauthSecret: string}>("POST", "/auth/reauth-secret")
    .then((res) => setReauthSecret(res.reauthSecret))
    .catch((e: unknown) => console.warn("Could not fetch reauth secret", e));
  return reauthSecretPromise;
};

export const reauth = async () => {
  const deviceId = storageWrapper.storageGet(StorageKey.DEVICE_ID);
  const reauthSecret = storageWrapper.storageGet(StorageKey.REAUTH_SECRET);
  if (!deviceId || !reauthSecret) return;
  const res = await request<{reauthSecret: string}>("POST", "/auth/reauth", {
    deviceId,
    reauthSecret,
  });
  setReauthSecret(res.reauthSecret);
};

export const startSync = async (userRepo: UserRepo) => {
  const vals = userRepo.vals.get();
  const userId = vals.id;
  const res = await request<{ok: boolean; deviceId: DeviceId; reauthSecret: string}>(
    "POST",
    `/auth/sync`,
    {userId}
  );
  if (res.ok) {
    setDeviceId(res.deviceId);
    setReauthSecret(res.reauthSecret);
    userRepo.vals.patch({isSyncing: true});
    const syncAllGroups = async () => {
      const groups = userRepo.Group.getAll();
      for (const group of Object.values(groups)) {
        await getGroupRepoLoader(group.id).withRepo(null, async (groupRepo) => {
          await oneOffSync(`${wsServerHost}/sync/group/${group.id}`, groupRepo.store);
          await syncAllLocalAssetsWithNewDeviceId(res.deviceId, groupRepo);
        });
      }
    };
    void syncAllGroups();
  }
};

export type UserChallengeResponse =
  | {ok: true; userId: UserId; deviceId: DeviceId; reauthSecret: string}
  | {ok: false; errorKey: "notFound" | "expired"};

export const getAddDeviceLink = async ({userRepo}: {userRepo: UserRepo}) => {
  const {isSyncing} = userRepo.vals.get();
  if (!isSyncing) await startSync(userRepo);
  const {secret} = await request<{secret: string}>("POST", "/auth/create-challenge/user");
  return `${location.origin}/sync/${secret}`;
};

export const acceptDeviceLink = async (secret: string) => {
  const res = await request<UserChallengeResponse>("POST", `/auth/accept-challenge/user`, {secret});
  if (res.ok) {
    setDeviceId(res.deviceId);
    setReauthSecret(res.reauthSecret);
  }
  return res;
};

export const getShareGroupLink = async ({
  groupId,
  userRepo,
}: {
  groupId: GroupId;
  userRepo: UserRepo;
}) => {
  const {isSyncing} = userRepo.vals.get();
  if (!isSyncing) await startSync(userRepo);
  const {secret} = await request<{secret: string}>("POST", "/auth/create-challenge/group", {
    groupId,
  });
  return `${location.origin}/add-group/${secret}`;
};

export type GroupChallengeResponse =
  | {ok: true; groupId: GroupId}
  | {ok: false; errorKey: "notFound" | "expired" | "alreadyAccess"};

export const acceptShareGroupLink = async (userRepo: UserRepo, secret: string) => {
  const {isSyncing} = userRepo.vals.get();
  if (!isSyncing) await startSync(userRepo);
  return request<GroupChallengeResponse>("POST", `/auth/accept-challenge/group`, {
    secret,
  });
};
